import { useEffect, useState } from "react";
import { useMerchant } from "@auth";
import { Download } from "react-bootstrap-icons";
import { CSVLink } from "react-csv";
import { arrayMove } from "@dnd-kit/sortable";
import {
  useColumnManager,
  useTableFilters,
  buildFilterPredicate,
  TableMenu,
  TableMenuItem,
} from "./shared";
import TableCore from "./shared/TableCore";

const ClientTable = ({ config, data }) => {
  const { merchant, hasPermission } = useMerchant();

  const shouldShowCol = (x) => {
    return (
      !x.hide &&
      (!x.permission || hasPermission(x.permission)) &&
      !(merchant?.customisationSettings?.hiddenColumnKeys ?? []).includes(
        `${config.key}.${x.key}`,
      )
    );
  };

  const [processedData, setProcessedData] = useState(null);
  const [selectedData, setSelectedData] = useState([]);
  const [sort, setSort] = useState(
    config.defaultSortKey || config.columns.filter((x) => x.sortFunc)[0]?.key,
  );
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const {
    columnPreferences,
    setColumnPreferences,
    managedColumns,
    resetColumns,
  } = useColumnManager(
    config.columns.filter((x) => shouldShowCol(x)),
    config.key,
  );

  const {
    filters,
    setFilters,
    addFilter,
    updateFilter,
    removeFilter,
    autoOpenKey,
    newFilterButtonRef,
    getColTitle,
  } = useTableFilters(config.key, managedColumns);

  const getQuickFilterValue = (quickFilter) => {
    if (!processedData) {
      return "...";
    }

    const qfPredicate = buildFilterPredicate(quickFilter.filters || []);
    const filtered = processedData.filter((x) => qfPredicate(x));

    return quickFilter.render(filtered.reduce(quickFilter.aggregate, 0));
  };

  const [pagination, setPagination] = useState({
    results: 0,
    from: 0,
    to: 0,
  });

  const step = 100;

  const paginationForward = () => {
    setPagination((prev) => {
      const newFrom = prev.from + step;
      const newTo = Math.min(prev.to + step, prev.results - 1);
      return newFrom < prev.results
        ? { ...prev, from: newFrom, to: newTo }
        : prev;
    });
  };

  const paginationBackward = () => {
    setPagination((prev) => {
      const newFrom = Math.max(prev.from - step, 0);
      const newTo = Math.min(newFrom + step - 1, prev.results - 1);
      return { ...prev, from: newFrom, to: newTo };
    });
  };

  useEffect(() => {
    if (!data) return;

    let newData = data;

    if (filters.length > 0) {
      const predicate = buildFilterPredicate(filters);
      newData = newData.filter((x) => predicate(x));
    }

    if (searchTerm) {
      newData = newData.filter((x) => config.search(x, searchTerm));
    }

    if (sort) {
      let sortFunc = managedColumns.find((x) => x.key === sort)?.sortFunc;

      if (sortFunc) {
        newData = newData.toSorted(
          (a, b) => sortFunc(a, b) * (sortDirection === "asc" ? 1 : -1),
        );
      }
    }

    setPagination({
      results: newData.length,
      from: 0,
      to: Math.min(newData.length, 100) - 1,
    });

    setProcessedData(newData);
  }, [data, sort, sortDirection, filters, searchTerm]);

  const handleSort = (key) => {
    let direction = "desc";
    if (sort === key) {
      direction = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(direction);
    } else {
      setSortDirection("desc");
      setSort(key);
    }
  };

  const isRowSelectable = (row) =>
    !config.isSelectable || config.isSelectable(row);
  const getVisibleRows = () =>
    processedData ? processedData.filter(isRowSelectable) : [];
  const areAllVisibleSelected = () => {
    const visible = getVisibleRows();
    return (
      visible.length > 0 &&
      visible.every((item) =>
        (selectedData || []).some((s) => s.id === item.id),
      )
    );
  };
  const onSelectAllChanged = () => {
    const visible = getVisibleRows();
    if (areAllVisibleSelected()) setSelectedData([]);
    else setSelectedData(visible);
  };
  const onSelectedChange = (x) => {
    setSelectedData((prevSelected) => {
      const isSelected = prevSelected.some((item) => item.id === x.id);
      return isSelected
        ? prevSelected.filter((item) => item.id !== x.id)
        : [...prevSelected, x];
    });
  };

  const handleColumnReorder = (activeId, overId) => {
    setColumnPreferences((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === activeId);
      const newIndex = prev.findIndex((p) => p.id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        position: idx,
      }));
    });
  };

  const headers = () =>
    managedColumns
      .filter((x) => !!x.key)
      .map((x) => ({ label: getColTitle(x), key: x.key }));

  const statistics = config.statistics
    ? config.statistics.map((stat) => ({
        label: stat.label,
        value: getQuickFilterValue(stat),
        subStatistics: stat.subStatistics?.map((sub) => ({
          label: sub.label,
          value: getQuickFilterValue(sub),
        })),
      }))
    : null;

  const visibleMenuItems = (config.menu || []).filter(
    (item) =>
      !item.hidden && (!item.permission || hasPermission(item.permission)),
  );

  const menu = (
    <TableMenu
      columnsManager={{
        data: columnPreferences,
        setData: setColumnPreferences,
        onReset: resetColumns,
      }}
    >
      {visibleMenuItems.map((item, i) => (
        <TableMenuItem
          key={`menu-${i}`}
          icon={item.icon}
          subtext={item.subtext}
          disabled={item.disabled}
          onClick={() => !item.disabled && item.onClick?.()}
        >
          {item.label}
        </TableMenuItem>
      ))}
      {processedData && config.csvExport !== false && (
        <CSVLink
          data={processedData}
          headers={headers()}
          filename={`${config.key}_${new Date().getTime()}.csv`}
          target="_blank"
          className="block"
        >
          <TableMenuItem icon={Download}>Export</TableMenuItem>
        </CSVLink>
      )}
    </TableMenu>
  );

  return (
    <>
      <TableCore
        config={config}
        rows={processedData?.slice(pagination.from, pagination.to + 1)}
        rawData={data}
        loading={!data}
        managedColumns={managedColumns}
        filters={filters}
        setFilters={setFilters}
        addFilter={addFilter}
        updateFilter={updateFilter}
        removeFilter={removeFilter}
        autoOpenKey={autoOpenKey}
        newFilterButtonRef={newFilterButtonRef}
        getColTitle={getColTitle}
        sort={sort}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchTerm={searchTerm}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
        isAllChecked={areAllVisibleSelected()}
        isRowChecked={(row) => selectedData.some((y) => y.id === row.id)}
        onSelectAllChanged={onSelectAllChanged}
        onSelectedChange={onSelectedChange}
        statistics={statistics}
        pagination={{
          onPrev: paginationBackward,
          onNext: paginationForward,
          prevDisabled: pagination.from === 0,
          nextDisabled: pagination.to >= pagination.results - 1,
          label: `${pagination.from + 1} - ${pagination.to + 1} of ${pagination.results} result${pagination.results !== 1 ? "s" : ""}`,
        }}
        menu={menu}
        onColumnReorder={handleColumnReorder}
      />
    </>
  );
};

export default ClientTable;
